import { useEffect, useState } from "react";
import { ShieldCheck, Loader2, Lock, Crown, CheckCircle2, XCircle, Server, KeyRound } from "lucide-react";
import CockpitLayout from "@/components/admin/CockpitLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const YesNo = ({ value }) =>
  value ? (
    <span className="inline-flex items-center gap-1 text-[12.5px] font-bold text-status-success" data-testid="yesno-yes">
      <CheckCircle2 size={14} /> JA
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-[12.5px] font-bold text-red-600" data-testid="yesno-no">
      <XCircle size={14} /> NEIN
    </span>
  );

export default function PlattformKonten() {
  const { user } = useAuth();
  const isPlatformAdmin = !!user?.is_platform_admin;
  const [state, setState] = useState("loading"); // loading | done | error
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isPlatformAdmin) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/auth/platform/overview");
        if (alive) {
          setData(data);
          setState("done");
        }
      } catch (e) {
        if (alive) {
          setError(formatApiError(e.response?.data?.detail) || "Laden fehlgeschlagen.");
          setState("error");
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [isPlatformAdmin]);

  if (!isPlatformAdmin) {
    return (
      <CockpitLayout active="plattform-konten" title="Plattform-Konten">
        <div className="mx-auto max-w-md py-16 text-center" data-testid="plattform-konten-no-access">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
            <Lock className="text-red-500" />
          </span>
          <h1 className="mt-4 text-[22px] font-black text-fundio-ink">Kein Zugriff</h1>
          <p className="mt-2 text-sm text-fundio-slate">
            Dieser Bereich ist ausschliesslich für den FUNDIO Platform-Super-Admin.
          </p>
        </div>
      </CockpitLayout>
    );
  }

  const cfg = data?.owner_config;
  const ctrl = data?.control_managed_operators;

  return (
    <CockpitLayout active="plattform-konten" title="Plattform-Konten">
      <div className="mx-auto max-w-4xl" data-testid="plattform-konten-panel">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-fundio-gold/10">
            <ShieldCheck className="text-fundio-gold" size={22} />
          </span>
          <div>
            <h1 className="text-[22px] font-black text-fundio-ink">Plattform-Konten &amp; Owner-Status</h1>
            <p className="text-[13px] text-fundio-slate">Nur Platform-Super-Admin · rein lesend, keine Geheimnisse</p>
          </div>
        </div>

        <div
          className="mt-5 flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3"
          data-testid="plattform-konten-readonly-warning"
        >
          <ShieldCheck className="mt-0.5 shrink-0 text-amber-600" size={18} />
          <div className="text-[13px] leading-relaxed text-amber-900">
            <strong className="font-black uppercase tracking-wide">READ ONLY.</strong> Diese Ansicht zeigt nur
            Status (Rollen, Aktiv, Passwortwechsel, 2FA) und Konfigurations-Präsenz als JA/NEIN. Es werden{" "}
            <strong>keine Passwörter, Hashes, TOTP-Secrets, Reset-Codes oder Service-Tokens</strong> angezeigt.
          </div>
        </div>

        {state === "loading" && (
          <div className="mt-8 flex items-center gap-2 text-fundio-slate" data-testid="plattform-konten-loading">
            <Loader2 className="animate-spin" size={18} /> Lade…
          </div>
        )}
        {state === "error" && (
          <p className="mt-6 flex items-center gap-2 text-[13px] font-medium text-red-600" data-testid="plattform-konten-error">
            <XCircle size={15} /> {error}
          </p>
        )}

        {state === "done" && data && (
          <div className="mt-6 space-y-6">
            {/* Owner config status */}
            <section className="overflow-hidden rounded-2xl border border-fundio-border bg-white" data-testid="owner-config">
              <header className="flex items-center gap-2 border-b border-fundio-border bg-fundio-subtle px-4 py-3">
                <KeyRound size={16} className="text-fundio-gold" />
                <h2 className="text-[14px] font-black text-fundio-ink">Owner-Konfiguration (Runtime)</h2>
              </header>
              <dl className="divide-y divide-fundio-border">
                <div className="flex items-center justify-between px-4 py-2.5" data-testid="cfg-owner-email">
                  <dt className="text-[13px] text-fundio-slate">Aufgelöster Owner</dt>
                  <dd className="font-mono text-[13px] font-semibold text-fundio-ink">{cfg?.resolved_owner_email}</dd>
                </div>
                <div className="flex items-center justify-between px-4 py-2.5" data-testid="cfg-email-configured">
                  <dt className="text-[13px] text-fundio-slate">E-Mail konfiguriert ({cfg?.email_source})</dt>
                  <dd><YesNo value={cfg?.email_configured} /></dd>
                </div>
                <div className="flex items-center justify-between px-4 py-2.5" data-testid="cfg-password-configured">
                  <dt className="text-[13px] text-fundio-slate">Passwort konfiguriert ({cfg?.password_source || "—"})</dt>
                  <dd><YesNo value={cfg?.password_configured} /></dd>
                </div>
                <div className="flex items-center justify-between px-4 py-2.5" data-testid="cfg-reset-flag">
                  <dt className="text-[13px] text-fundio-slate">Passwort-Reset-Flag aktiv</dt>
                  <dd><YesNo value={cfg?.password_reset_flag_enabled} /></dd>
                </div>
                <div className="px-4 py-2.5 text-[12px] text-fundio-slate" data-testid="cfg-keys">
                  Kanonisch: <span className="font-mono">{(cfg?.canonical_keys || []).join(", ")}</span> · Kompatibel:{" "}
                  <span className="font-mono">{(cfg?.compat_keys || []).join(", ")}</span>
                </div>
                <div className="px-4 py-2.5 text-[12px] leading-relaxed text-fundio-slate" data-testid="cfg-central-assignment">
                  <strong className="text-fundio-ink">Zentrale Teamzuordnung:</strong> Owner-Identität wird zentral über{" "}
                  <span className="font-mono">SUPER_ADMIN_EMAIL</span> / <span className="font-mono">SUPER_ADMIN_PASSWORD</span>{" "}
                  (Emergent „Verwalten › Geheimnisse") gesetzt; leer = nicht gesetzt (Alias <span className="font-mono">ADMIN_*</span>{" "}
                  bleibt aktiv, identischer Owner). Operator-Geheimnisse (<span className="font-mono">ADMIN_OPS_*</span>) liegen
                  ausschliesslich CONTROL-seitig.
                </div>
              </dl>
            </section>

            {/* Local app accounts */}
            <section className="overflow-hidden rounded-2xl border border-fundio-border bg-white" data-testid="local-accounts">
              <header className="flex items-center justify-between border-b border-fundio-border bg-fundio-subtle px-4 py-3">
                <h2 className="text-[14px] font-black text-fundio-ink">Lokale APP-Konten</h2>
                <span className="text-[12px] text-fundio-slate">
                  {data.counts?.total_accounts} Konten · {data.counts?.platform_owners} Owner
                </span>
              </header>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-[13px]">
                  <thead className="bg-white text-[11.5px] uppercase tracking-wide text-fundio-slate">
                    <tr>
                      <th className="px-4 py-2 font-semibold">Name / E-Mail</th>
                      <th className="px-4 py-2 font-semibold">Rolle</th>
                      <th className="px-4 py-2 font-semibold">Owner</th>
                      <th className="px-4 py-2 font-semibold">Aktiv</th>
                      <th className="px-4 py-2 font-semibold">PW-Wechsel</th>
                      <th className="px-4 py-2 font-semibold">2FA</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-fundio-border">
                    {(data.accounts || []).map((a) => (
                      <tr key={a.id} data-testid={`account-row-${a.id}`} className="align-middle">
                        <td className="px-4 py-2.5">
                          <div className="font-semibold text-fundio-ink">{a.name || "—"}</div>
                          <div className="font-mono text-[12px] text-fundio-slate">{a.email}</div>
                        </td>
                        <td className="px-4 py-2.5">{a.role}</td>
                        <td className="px-4 py-2.5">
                          {a.is_platform_admin ? (
                            <span className="inline-flex items-center gap-1 font-bold text-fundio-gold" data-testid="owner-badge">
                              <Crown size={14} /> Owner
                            </span>
                          ) : (
                            <span className="text-fundio-slate">—</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5"><YesNo value={a.active} /></td>
                        <td className="px-4 py-2.5"><YesNo value={a.must_change_password} /></td>
                        <td className="px-4 py-2.5"><YesNo value={a.two_factor_enabled} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* CONTROL-managed operators */}
            <section className="overflow-hidden rounded-2xl border border-fundio-border bg-white" data-testid="control-operators">
              <header className="flex items-center gap-2 border-b border-fundio-border bg-fundio-subtle px-4 py-3">
                <Server size={16} className="text-fundio-slate" />
                <h2 className="text-[14px] font-black text-fundio-ink">CONTROL-verwaltete Operatoren</h2>
              </header>
              <div className="px-4 py-3">
                <p className="text-[13px] text-fundio-slate">{ctrl?.note}</p>
                <div className="mt-3 flex flex-wrap gap-2" data-testid="control-key-names">
                  {(ctrl?.credential_key_names || []).map((k) => (
                    <span key={k} className="rounded-md bg-fundio-subtle px-2 py-1 font-mono text-[12px] text-fundio-ink">
                      {k}
                    </span>
                  ))}
                </div>
                <p className="mt-3 text-[12px] text-fundio-slate">
                  Verwaltet in: <strong>{ctrl?.managed_in}</strong> · Nur Schlüsselnamen, keine Werte.
                </p>
              </div>
            </section>
          </div>
        )}
      </div>
    </CockpitLayout>
  );
}
